import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import api from "../../services/api";

export default function PanelSuperAdmin() {
  const { logout } = useAuth();
  const [vista, setVista] = useState("resumen");
  const [tenants, setTenants] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modalResetPass, setModalResetPass] = useState(null);
  const [nuevaClave, setNuevaClave] = useState("");
  const [modalEditarUsuario, setModalEditarUsuario] = useState(null);
  const [formEditarUsuario, setFormEditarUsuario] = useState({ nombre: "", email: "" });

  const [modalNuevoTenant, setModalNuevoTenant] = useState(false);
  const [formTenant, setFormTenant] = useState({ nombre: "", slug: "", plan: "basico", monto_mensual: "50000" });

  useEffect(() => {
    cargarTodo();
  }, []);

  const cargarTodo = async () => {
    setLoading(true);
    try {
      const [tRes, uRes, eRes, lRes] = await Promise.all([
        api.get("/superadmin/tenants"),
        api.get("/superadmin/usuarios"),
        api.get("/superadmin/estadisticas"),
        api.get("/superadmin/logs"),
      ]);
      setTenants(tRes.data);
      setUsuarios(uRes.data);
      setEstadisticas(eRes.data);
      setLogs(lRes.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const toggleTenant = async (id) => {
    try {
      await api.patch(`/superadmin/tenants/${id}/toggle`);
      cargarTodo();
    } catch (err) {
      console.error(err);
      alert("Error al cambiar estado del tenant");
    }
  };

  const toggleUsuario = async (id) => {
    try {
      await api.patch(`/superadmin/usuarios/${id}/toggle`);
      cargarTodo();
    } catch (err) {
      console.error(err);
      alert("Error al cambiar estado del usuario");
    }
  };

  const resetPassword = async () => {
    if (!nuevaClave || nuevaClave.length < 6) {
      alert("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    try {
      await api.patch(`/superadmin/usuarios/${modalResetPass.id}/password`, { password: nuevaClave });
      setModalResetPass(null);
      setNuevaClave("");
      alert("Contraseña actualizada correctamente");
    } catch (err) {
      console.error(err);
      alert("Error al resetear contraseña");
    }
  };

  const guardarEdicionUsuario = async () => {
    if (!formEditarUsuario.nombre || !formEditarUsuario.email) {
      alert("Completa nombre y email");
      return;
    }
    try {
      await api.put(`/superadmin/usuarios/${modalEditarUsuario.id}`, formEditarUsuario);
      setModalEditarUsuario(null);
      cargarTodo();
    } catch (err) {
      console.error(err);
      alert("Error al editar usuario: " + (err.response?.data?.message || "revisa los datos"));
    }
  };

  const crearTenant = async () => {
    if (!formTenant.nombre || !formTenant.slug) {
      alert("Nombre y slug son obligatorios");
      return;
    }
    try {
      await api.post("/superadmin/tenants", formTenant);
      setModalNuevoTenant(false);
      setFormTenant({ nombre: "", slug: "", plan: "basico", monto_mensual: "50000" });
      cargarTodo();
    } catch (err) {
      console.error(err);
      alert("Error al crear tenant: " + (err.response?.data?.message || "revisa los datos"));
    }
  };

  const inputClass = "w-full mt-1 bg-gray-50 border border-[#E5E9F0] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#1A56DB] focus:ring-1 focus:ring-[#1A56DB]";
  const labelClass = "text-xs text-gray-500 uppercase tracking-wider font-medium";

  const menuItems = [
    { key: "resumen", label: "Resumen", icon: "📊" },
    { key: "tenants", label: "Negocios", icon: "🏢" },
    { key: "usuarios", label: "Usuarios", icon: "👥" },
    { key: "logs", label: "Historial", icon: "🕐" },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F1F4F9] flex items-center justify-center">
        <div className="text-[#1A56DB] text-sm font-medium">Cargando panel SuperAdmin...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F1F4F9] flex">
      {/* SIDEBAR */}
      <div className="w-64 bg-gradient-to-b from-[#0F1F4D] to-[#1A56DB] flex-shrink-0 flex flex-col">
        <div className="p-5 border-b border-white/10">
          <div className="flex items-center gap-2">
            <img src="/logo-rivaldy.png" alt="Rivaldy" className="w-9 h-9 object-contain" />
            <div>
              <div className="text-white font-bold text-sm leading-tight">Rivaldy</div>
              <div className="text-blue-300 text-[10px] uppercase tracking-wider">SuperAdmin</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1">
          {menuItems.map((item) => (
            <button
              key={item.key}
              onClick={() => setVista(item.key)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                vista === item.key
                  ? "bg-white/10 text-white shadow-sm border-l-2 border-blue-400"
                  : "text-blue-200/70 hover:bg-white/5 hover:text-white"
              }`}
            >
              <span className="text-base">{item.icon}</span>
              {item.label}
            </button>
          ))}
        </nav>

        <div className="p-3 border-t border-white/10">
          <button
            onClick={logout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-blue-200/70 hover:bg-white/5 hover:text-white transition-all"
          >
            <span className="text-base">🚪</span>
            Salir
          </button>
        </div>
      </div>

      {/* CONTENIDO PRINCIPAL */}
      <div className="flex-1 overflow-y-auto">
        <div className="bg-white border-b border-[#E5E9F0] px-6 py-4 flex items-center justify-between sticky top-0 z-10">
          <h1 className="text-lg font-semibold text-gray-900">
            {menuItems.find((m) => m.key === vista)?.label}
          </h1>
        </div>

        <div className="p-6">
          {/* RESUMEN */}
          {vista === "resumen" && estadisticas && (
            <div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div className="bg-white border border-[#E5E9F0] rounded-xl p-4 text-center shadow-sm">
                  <div className="text-2xl font-bold text-[#1A56DB]">{estadisticas.tenants_totales}</div>
                  <div className="text-xs text-gray-400 mt-1 uppercase tracking-wider">Negocios</div>
                </div>
                <div className="bg-white border border-[#E5E9F0] rounded-xl p-4 text-center shadow-sm">
                  <div className="text-2xl font-bold text-green-600">{estadisticas.tenants_activos}</div>
                  <div className="text-xs text-gray-400 mt-1 uppercase tracking-wider">Activos</div>
                </div>
                <div className="bg-white border border-[#E5E9F0] rounded-xl p-4 text-center shadow-sm">
                  <div className="text-2xl font-bold text-gray-900">{estadisticas.usuarios_totales}</div>
                  <div className="text-xs text-gray-400 mt-1 uppercase tracking-wider">Usuarios</div>
                </div>
                <div className="bg-white border border-[#E5E9F0] rounded-xl p-4 text-center shadow-sm">
                  <div className="text-2xl font-bold text-gray-900">{estadisticas.pedidos_totales}</div>
                  <div className="text-xs text-gray-400 mt-1 uppercase tracking-wider">Pedidos totales</div>
                </div>
              </div>

              <div className="bg-white border border-[#E5E9F0] rounded-xl p-4 mb-4 shadow-sm">
                <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Ingreso mensual esperado</div>
                <div className="text-2xl font-bold text-green-700">
                  ${parseInt(estadisticas.ingreso_mensual_esperado || 0).toLocaleString("es-CL")}
                </div>
                <div className="text-xs text-gray-400 mt-1">Suma de planes de todos los tenants activos</div>
              </div>

              <div className="bg-white border border-[#E5E9F0] rounded-xl overflow-x-auto shadow-sm">
                <div className="px-4 py-3 border-b border-[#E5E9F0]">
                  <h3 className="text-sm font-semibold text-gray-900">Pedidos por negocio</h3>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-400 uppercase border-b border-[#E5E9F0]">
                      <th className="text-left px-4 py-2">Tenant ID</th>
                      <th className="text-left px-4 py-2">Pedidos</th>
                      <th className="text-left px-4 py-2">Monto total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {estadisticas.pedidos_por_tenant.map((p) => (
                      <tr key={p.tenant_id} className="border-b border-[#E5E9F0]">
                        <td className="px-4 py-2">{p.tenant_id}</td>
                        <td className="px-4 py-2">{p.total}</td>
                        <td className="px-4 py-2">${parseInt(p.monto_total || 0).toLocaleString("es-CL")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TENANTS */}
          {vista === "tenants" && (
            <div>
              <div className="flex justify-end mb-3">
                <button
                  onClick={() => setModalNuevoTenant(true)}
                  className="bg-gradient-to-r from-[#0F1F4D] to-[#1A56DB] text-white text-sm font-medium px-4 py-2 rounded-lg shadow-sm"
                >
                  + Nuevo negocio
                </button>
              </div>
              <div className="bg-white border border-[#E5E9F0] rounded-xl overflow-x-auto shadow-sm">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-400 uppercase border-b border-[#E5E9F0]">
                      <th className="text-left px-4 py-2">Nombre</th>
                      <th className="text-left px-4 py-2">Slug</th>
                      <th className="text-left px-4 py-2">Plan</th>
                      <th className="text-left px-4 py-2">Usuarios</th>
                      <th className="text-left px-4 py-2">Pedidos</th>
                      <th className="text-left px-4 py-2">Mensual</th>
                      <th className="text-left px-4 py-2">Estado</th>
                      <th className="text-right px-4 py-2">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenants.map((t) => (
                      <tr key={t.id} className="border-b border-[#E5E9F0]">
                        <td className="px-4 py-3 font-medium text-gray-900">{t.nombre}</td>
                        <td className="px-4 py-3 text-gray-500">{t.slug}</td>
                        <td className="px-4 py-3 capitalize">{t.plan}</td>
                        <td className="px-4 py-3">{t.users_count}</td>
                        <td className="px-4 py-3">{t.total_pedidos} <span className="text-amber-600 text-xs">({t.pedidos_pendientes} pend.)</span></td>
                        <td className="px-4 py-3">${parseInt(t.monto_mensual || 0).toLocaleString("es-CL")}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${
                            t.estado === "activo" ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-600 border-red-200"
                          }`}>
                            {t.estado}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => toggleTenant(t.id)}
                            className={`text-xs font-medium px-3 py-1.5 rounded-lg ${
                              t.estado === "activo" ? "bg-red-50 text-red-600 hover:bg-red-100" : "bg-green-50 text-green-700 hover:bg-green-100"
                            }`}
                          >
                            {t.estado === "activo" ? "Suspender" : "Reactivar"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* USUARIOS */}
          {vista === "usuarios" && (
            <div className="bg-white border border-[#E5E9F0] rounded-xl overflow-x-auto shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-400 uppercase border-b border-[#E5E9F0]">
                    <th className="text-left px-4 py-2">Nombre</th>
                    <th className="text-left px-4 py-2">Email</th>
                    <th className="text-left px-4 py-2">Rol</th>
                    <th className="text-left px-4 py-2">Negocio</th>
                    <th className="text-left px-4 py-2">Estado</th>
                    <th className="text-right px-4 py-2">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {usuarios.map((u) => (
                    <tr key={u.id} className="border-b border-[#E5E9F0]">
                      <td className="px-4 py-3 font-medium text-gray-900">{u.nombre}</td>
                      <td className="px-4 py-3 text-gray-500">{u.email}</td>
                      <td className="px-4 py-3 capitalize">{u.rol}</td>
                      <td className="px-4 py-3">{u.tenant?.nombre || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${
                          u.activo ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-600 border-red-200"
                        }`}>
                          {u.activo ? "Activo" : "Bloqueado"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right space-x-2">
                        {u.rol !== "superadmin" && (
                          <>
                            <button
                              onClick={() => { setModalEditarUsuario(u); setFormEditarUsuario({ nombre: u.nombre, email: u.email }); }}
                              className="text-gray-600 text-xs font-medium hover:underline"
                            >
                              Editar
                            </button>
                            <button
                              onClick={() => setModalResetPass(u)}
                              className="text-[#1A56DB] text-xs font-medium hover:underline"
                            >
                              Resetear clave
                            </button>
                            <button
                              onClick={() => toggleUsuario(u.id)}
                              className={`text-xs font-medium px-3 py-1.5 rounded-lg ${
                                u.activo ? "bg-red-50 text-red-600 hover:bg-red-100" : "bg-green-50 text-green-700 hover:bg-green-100"
                              }`}
                            >
                              {u.activo ? "Bloquear" : "Activar"}
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* LOGS */}
          {vista === "logs" && (
            <div className="bg-white border border-[#E5E9F0] rounded-xl overflow-hidden shadow-sm">
              <div className="px-4 py-3 border-b border-[#E5E9F0]">
                <h3 className="text-sm font-semibold text-gray-900">Historial de actividad (últimos 200)</h3>
              </div>
              <div className="divide-y divide-[#E5E9F0] max-h-[600px] overflow-y-auto">
                {logs.length === 0 && (
                  <div className="text-center text-gray-400 py-8 text-sm">Sin actividad registrada aún</div>
                )}
                {logs.map((l) => (
                  <div key={l.id} className="px-4 py-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-[#1A56DB] capitalize">{(l.accion || "").replace(/_/g, " ")}</span>
                      <span className="text-xs text-gray-400">{new Date(l.created_at).toLocaleString("es-CL")}</span>
                    </div>
                    <div className="text-sm text-gray-700 mt-0.5">{l.detalle}</div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      Por: {l.user?.nombre || "Sistema"} {l.tenant ? `· ${l.tenant.nombre}` : ""} {l.ip ? `· IP: ${l.ip}` : ""}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* MODAL RESET PASSWORD */}
      {modalResetPass && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center backdrop-blur-sm p-4">
          <div className="bg-white border border-[#E5E9F0] rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <h3 className="text-gray-900 font-semibold text-lg mb-1">Resetear contraseña</h3>
            <p className="text-gray-400 text-sm mb-4">{modalResetPass.nombre} · {modalResetPass.email}</p>
            <div className="mb-4">
              <label className={labelClass}>Nueva contraseña</label>
              <input
                type="text"
                value={nuevaClave}
                onChange={(e) => setNuevaClave(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                className={inputClass}
              />
            </div>
            <div className="flex gap-2">
              <button onClick={resetPassword} className="flex-1 bg-gradient-to-r from-[#0F1F4D] to-[#1A56DB] text-white font-semibold py-2.5 rounded-lg">
                Guardar
              </button>
              <button onClick={() => { setModalResetPass(null); setNuevaClave(""); }} className="bg-gray-100 text-gray-600 px-4 rounded-lg">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL EDITAR USUARIO */}
      {modalEditarUsuario && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center backdrop-blur-sm p-4">
          <div className="bg-white border border-[#E5E9F0] rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <h3 className="text-gray-900 font-semibold text-lg mb-4">Editar usuario</h3>
            <div className="space-y-3 mb-4">
              <div>
                <label className={labelClass}>Nombre</label>
                <input
                  type="text"
                  value={formEditarUsuario.nombre}
                  onChange={(e) => setFormEditarUsuario({ ...formEditarUsuario, nombre: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Email</label>
                <input
                  type="email"
                  value={formEditarUsuario.email}
                  onChange={(e) => setFormEditarUsuario({ ...formEditarUsuario, email: e.target.value })}
                  className={inputClass}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={guardarEdicionUsuario} className="flex-1 bg-gradient-to-r from-[#0F1F4D] to-[#1A56DB] text-white font-semibold py-2.5 rounded-lg">
                Guardar
              </button>
              <button onClick={() => setModalEditarUsuario(null)} className="bg-gray-100 text-gray-600 px-4 rounded-lg">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL NUEVO TENANT */}
      {modalNuevoTenant && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center backdrop-blur-sm p-4">
          <div className="bg-white border border-[#E5E9F0] rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <h3 className="text-gray-900 font-semibold text-lg mb-4">Nuevo negocio (tenant)</h3>
            <div className="space-y-3 mb-4">
              <div>
                <label className={labelClass}>Nombre del negocio</label>
                <input type="text" value={formTenant.nombre} onChange={(e) => setFormTenant({ ...formTenant, nombre: e.target.value })} className={inputClass} placeholder="Ej: Pastelería Dulce Vida" />
              </div>
              <div>
                <label className={labelClass}>Slug (subdominio, sin espacios)</label>
                <input type="text" value={formTenant.slug} onChange={(e) => setFormTenant({ ...formTenant, slug: e.target.value.toLowerCase().replace(/\s/g, '') })} className={inputClass} placeholder="ej: dulcevida" />
              </div>
              <div>
                <label className={labelClass}>Plan</label>
                <select value={formTenant.plan} onChange={(e) => setFormTenant({ ...formTenant, plan: e.target.value })} className={inputClass}>
                  <option value="basico">Básico</option>
                  <option value="pro">Pro</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Monto mensual ($)</label>
                <input type="number" value={formTenant.monto_mensual} onChange={(e) => setFormTenant({ ...formTenant, monto_mensual: e.target.value })} className={inputClass} />
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={crearTenant} className="flex-1 bg-gradient-to-r from-[#0F1F4D] to-[#1A56DB] text-white font-semibold py-2.5 rounded-lg">
                Crear
              </button>
              <button onClick={() => setModalNuevoTenant(false)} className="bg-gray-100 text-gray-600 px-4 rounded-lg">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
